import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import DataGrid from 'react-data-grid';
import clsx from 'clsx';
import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { selectLoading, changeLoadingStatus, changeSuccessStatus } from '../../store/layoutSlice';
import { selectEditedIndexes, setEditedIndexes, setHasUnsavedChanges } from '../../store/companySlice';
import TagSelector from './TagSelector';
import SaveButton from './SaveButton';
import ExportButton from './ExportButton';
import ActionableButton from './ActionableButton';
import GridColumnHeader from './GridColumnHeader';
import GridPagination from './GridPagination';

const useStyles = makeStyles((theme) => ({
  dataGrid: {
    height: '45vh',
  },
  elementHidden: {
    display: 'none',
  },
  visibilityHidden: {
    visibility: 'hidden',
  },
  gridBottom: {
    marginBottom: '-12px',
  },
  unsavedChangesMessage: {
    color: theme.palette.error.main,
    marginTop: '1rem',
    marginBottom: '1rem',
  },
}));

export default function BaseDataGrid({
  id,
  columns,
  selectData,
  fetchData,
  updateData,
  saveData,
  toolbar,
  ...props
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const data = useSelector(selectData);
  const loading = useSelector(selectLoading);
  const editedIndexes = useSelector(selectEditedIndexes);

  // Local State

  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [[sortColumn, sortDirection], setSort] = useState(['id', 'NONE']);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [expandedGroupIds, setExpandedGroupIds] = useState(() => new Set());
  const [options, setOptions] = useState({
    actionable: false,
    grouping: true,
    filtering: false,
    sorting: true,
    resizable: true,
    pageSize: -1,
  });

  // Grid Features

  const gridColumns = columns.map(
    (column) => ({ ...column, headerRenderer: column.headerRenderer || GridColumnHeader }),
  );

  const groupBy = useMemo(() => (Array.isArray(selectedOptions)
    ? selectedOptions.map((option) => option) : undefined), [selectedOptions]);

  const rowGrouper = (rows, key) => rows.reduce((groupedRows, row) => {
    (groupedRows[row[key]] = groupedRows[row[key]] || []).push(row);
    return groupedRows;
  }, {});

  const sortRows = (rows, columnName) => {
    if (rows.length > 0) {
      switch (typeof rows[0][columnName]) {
        case 'string':
          return rows.sort((a, b) => a[columnName].toString().localeCompare(b[columnName]));
        case 'number':
          return rows.sort((a, b) => a[columnName] - b[columnName]);
        default:
          return rows;
      }
    } else {
      return rows;
    }
  };

  // eslint-disable-next-line no-unused-vars
  const isActionableItem = (cellValue) => !cellValue && cellValue !== 0;

  // Memoized Callbacks

  const actionableData = useMemo(() => (data && data.filter((row) => Object.entries(row).reduce(
    // eslint-disable-next-line no-unused-vars
    (isActionableRow, [columnName, cellValue]) => !options.actionable
    || (isActionableRow || isActionableItem(cellValue)),
    false,
  ))) || [], [data, options]);

  const sortedData = useMemo(() => {
    if (sortDirection === 'NONE') return actionableData;

    let sortedRows = [...actionableData];

    sortedRows = sortRows(sortedRows, sortColumn);

    return sortDirection === 'DESC' ? sortedRows.reverse() : sortedRows;
  }, [actionableData, sortDirection, sortColumn]);

  // Side Effects

  useEffect(() => {
    if (loading) {
      setTimeout(() => {
        dispatch(fetchData());
        dispatch(changeSuccessStatus(true));
        dispatch(changeLoadingStatus(false));
        dispatch(setEditedIndexes({ id, indexes: new Set() }));
      }, 0);
    }
  }, [dispatch, id, fetchData, loading]);

  useEffect(() => {
    setTimeout(() => {
      dispatch(changeLoadingStatus(true));
    }, 0);
  }, [dispatch]);

  useEffect(() => {
    setCurrentPageData(options.pageSize !== -1 ? sortedData.slice(
      currentPage * options.pageSize,
      (currentPage + 1) * options.pageSize,
    ) : sortedData);
  }, [dispatch, currentPage, sortedData, options]);

  // Handlers

  const handleSort = useCallback((columnKey, direction) => {
    setSort([columnKey, direction]);
  }, []);

  const updateEditedIndexes = (rows, editedColumns) => {
    const currentIndexes = editedIndexes[id] ? editedIndexes[id] : [];
    const newIndexes = editedColumns.indexes.map((index) => rows[(currentPage * options.pageSize) + index]);

    dispatch(setHasUnsavedChanges(true));
    dispatch(setEditedIndexes({ id, indexes: new Set([...currentIndexes, ...newIndexes]) }));
  };

  const handleRowsChange = (rows, editedColumns) => {
    updateEditedIndexes(rows, editedColumns);
    dispatch(updateData(options.pageSize !== -1
      ? [
        ...data.slice(0, currentPage * options.pageSize),
        ...rows,
        ...data.slice((currentPage + 1) * options.pageSize, data.length),
      ] : rows));
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleChangeRowsPerPage = (event) => {
    setOptions({ ...options, pageSize: event.target.value });
  };

  const handleSave = () => {
    const editedRows = [...editedIndexes[id]].map((index) => data[index]);

    if (saveData) {
      dispatch(saveData(editedRows));
    }
  };

  const toggleActionable = () => {
    setOptions({ ...options, actionable: !options.actionable });
  };

  return (
    <>
      <Grid container>
        <Grid item xs={4}>
          <TagSelector
            options={columns.map((column) => ({ label: column.name, value: column.key }))}
            label="Group By"
            placeholder="Select..."
            value={selectedOptions}
            onChange={setSelectedOptions}
            isHidden={!options.grouping}
          />
        </Grid>
        <Grid item align="right" xs={4}>
          {toolbar}
        </Grid>
        <Grid item align="right" xs={4}>
          <ActionableButton
            isHidden={!props.isActionableButtonEnabled}
            onClick={toggleActionable}
            isActive={options.actionable}
          />
          <ExportButton data={data} headers={columns.map((column) => ({ label: column.name, key: column.key }))} />
          <SaveButton handleSave={handleSave} />
        </Grid>
      </Grid>
      <DataGrid
        columns={gridColumns}
        rows={currentPageData}
        groupBy={groupBy}
        rowGrouper={rowGrouper}
        onSelectedRowsChange={handleRowsChange}
        onRowsChange={handleRowsChange}
        expandedGroupIds={expandedGroupIds}
        onExpandedGroupIdsChange={setExpandedGroupIds}
        rowHeight={30}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        defaultColumnOptions={{
          sortable: options.sorting,
          resizable: options.resizable,
          formatter: ({ row, column }) => (
            (column.editor) ? <span className="editable">{ row[column.key] || null }</span> : row[column.key] || null
          ),
        }}
        className={classes.dataGrid}
        {...props}
      />
      <Grid container className={classes.gridBottom}>
        <Grid align="left" item xs={12} md={6}>
          <p className={
              clsx((!editedIndexes[id] || editedIndexes[id].size === 0)
              && classes.visibilityHidden, classes.unsavedChangesMessage)
            }
          >
            You have unsaved changes on this table.
          </p>
        </Grid>
        <Grid align="right" item xs={12} md={6}>
          <GridPagination
            count={sortedData.length}
            page={currentPage}
            onChangePage={handlePageChange}
            rowsPerPage={options.pageSize}
            rowsPerPageOptions={[10, 50, { value: -1, label: 'All' }]}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </Grid>
      </Grid>
    </>
  );
}
